<template>
  <RevForm
    :initial-values
    method="POST"
    :validate="validate()"
    @on-submit-failed="handleSubmitFailed"
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <RevCard class="p-24">
        <h2
          v-if="withTitle"
          class="heading-3 mb-12 md:mb-24"
          data-qa="billing-form-title"
        >
          {{ i18n(translations.billingAddressTitle) }}
        </h2>
        <DynamicAddressFieldValidatorsErrorLogger
          :errors
          :form-values="values"
          logger-scope="CHECKOUT_BILLING"
        />
        <AddressFields
          v-model:city="values.city"
          v-model:company="values.company"
          v-model:country="values.country"
          v-model:first-name="values.firstName"
          v-model:first-name-pronunciation="values.firstNamePronunciation"
          v-model:last-name="values.lastName"
          v-model:last-name-pronunciation="values.lastNamePronunciation"
          v-model:postal-code="values.postalCode"
          v-model:state-or-province="values.stateOrProvince"
          v-model:street="values.street"
          v-model:street2="values.street2"
          :countries
          :default-country="MARKET_COUNTRY_CODE"
          :errors="allErrors(errors)"
          :feature-code="FeatureCode.WEB_CHECKOUT_BILLING"
          :optional-fields="['company']"
          @update:country="(country) => emit('country-change', country)"
        />

        <div v-if="shouldDisplayUserInformation(values.country)">
          <RevDivider class="my-16 md:my-24" />
          <h2 class="heading-3 mb-16">
            {{ i18n(translations.userInformationTitle) }}
          </h2>
          <UserInformationFields
            v-model:birthdate="values.birthdate"
            v-model:national-id="values.nationalId"
            :country="values.country"
            :errors
            :with-birthdate="withUserInformation"
            :with-national-id="userInformationWithNationalId(values.country)"
          />

          <div class="mt-20 flex flex-col items-stretch md:items-end">
            <RevButton
              data-qa="submit-button"
              :disabled="submitting"
              full-width="adaptive"
              :loading="submitting"
              type="submit"
              variant="primary"
            >
              {{ submitLabel }}
            </RevButton>
          </div>
        </div>

        <div
          v-if="
            !withUserInformation &&
            !shouldDisplayUserInformation(values.country)
          "
          class="mt-32 flex flex-col items-stretch md:items-end"
        >
          <RevButton
            data-qa="submit-button"
            :disabled="submitting"
            full-width="adaptive"
            :loading="submitting"
            type="submit"
            variant="primary"
          >
            {{ submitLabel }}
          </RevButton>
        </div>
      </RevCard>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'

import { Country } from '@backmarket/http-api'
import type { Bill } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import DynamicAddressFieldValidatorsErrorLogger from '@backmarket/nuxt-module-address/DynamicAddressFieldValidatorsErrorLogger.vue'
import { InputAddressBirthdateValidators } from '@backmarket/nuxt-module-address/InputAddressBirthdateValidators'
import { InputAddressNationalIdValidators } from '@backmarket/nuxt-module-address/InputAddressNationalIdValidators'
import UserInformationFields from '@backmarket/nuxt-module-address/UserInformationFields.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useDynamicAddressFieldValidators } from '@backmarket/nuxt-module-address/useDynamicAddressFieldValidators'
import { isNationalIdShownForBilling } from '@backmarket/nuxt-module-address/utils/nationalId/isNationalIdShownForBilling'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { omit } from '@backmarket/utils/object/omit'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { type ErrorsOf, RevForm, makeValidate } from '@ds/components/Form'

import { focusFirstErrorFormField } from '~/scopes/checkout/utils/focusFirstErrorFormField'

import translations from './BillingAddressForm.translations'
import type { FormValues } from './BillingAddressForm.types'

const props = withDefaults(
  defineProps<{
    submitting?: boolean
    address: Bill
    apiErrors: { [key: string]: string }
    countries?: {
      value: string
      label: string
    }[]
    withUserInformation?: boolean
    withTitle?: boolean
    submitButtonLabel?: string
    withNationalId?: boolean
  }>(),
  {
    submitting: false,
    withUserInformation: false,
    withTitle: true,
    countries: () => [],
    submitButtonLabel: '',
    withNationalId: true,
  },
)
const i18n = useI18n()

// @TODO: Need to do this cast because MarketCountryCode and Country are not compatible
const MARKET_COUNTRY_CODE = useMarketplace().market
  .countryCode as unknown as Country

const { getDynamicAddressFieldValidators } = useDynamicAddressFieldValidators()

const initialValues = reactive<FormValues>({
  city: props.address.city ?? '',
  company: props.address.company ?? '',
  country: (props.address.country as Country) ?? '',
  firstName: props.address.firstName ?? '',
  lastName: props.address.lastName ?? '',
  nationalId: props.address.customerIdNumber ?? '',
  postalCode: props.address.postalCode ?? '',
  stateOrProvince: props.address.stateOrProvince ?? '',
  street: props.address.street ?? '',
  street2: props.address.street2 ?? '',
  ...insertIf(props.withUserInformation, {
    birthdate: props.address.birthdate ?? '',
  }),
  ...insertIf(props.address.country === Country.JP, {
    firstNamePronunciation: props.address.firstNamePronunciation ?? '',
    lastNamePronunciation: props.address.lastNamePronunciation ?? '',
  }),
})

const emit = defineEmits<{
  submit: [values: Omit<FormValues, 'nationalId'>]
  'submit-failed': [errors: ErrorsOf<Omit<FormValues, 'nationalId'>>]
  'country-change': [country?: string]
}>()

emit('country-change', initialValues.country)

const addressValidation = await getDynamicAddressFieldValidators({
  featureFlag: 'FF_ENABLE_ADDRESS_FIELD_VALIDATION_CHECKOUT_BILLING',
  initialFormValues: initialValues,
  metadata: {
    scope: 'customer',
  },
})

const validators = {
  ...addressValidation,
  ...insertIf(props.withUserInformation, {
    birthdate: InputAddressBirthdateValidators(i18n),
    nationalId: InputAddressNationalIdValidators(i18n),
  }),
}
// Memoize the validate function to return the same instance across renders.
// This is needed because the RevForm component watches props.validate and executes makeErrorsVisible()
// every time the signature of the validators changed.
const validateFn = makeValidate<FormValues>(validators)

function validate() {
  return validateFn
}

const submitLabel = computed(() => {
  return props.submitButtonLabel || i18n(translations.submit)
})

const allErrors = (errors: Partial<Record<keyof FormValues, string>>) => {
  return { ...errors, ...props.apiErrors }
}

const userInformationWithNationalId = (
  selectedCountry: FormValues['country'],
) => {
  if (!props.withNationalId) return false

  return !!selectedCountry && isNationalIdShownForBilling(selectedCountry)
}

const handleSubmitFailed = ({ errors }: { errors: ErrorsOf<FormValues> }) => {
  focusFirstErrorFormField(errors)

  emit('submit-failed', {
    ...omit(errors, 'nationalId'),
    ...errors,
  })
}

const handleSubmit = (values: FormValues) => {
  emit('submit', {
    ...omit(values, 'nationalId'),
    ...insertIf(
      !!values.country &&
        isNationalIdShownForBilling(values.country) &&
        !isEmpty(values.nationalId),
      {
        customerIdNumber: values.nationalId,
      },
    ),
  })
}

const shouldDisplayUserInformation = (country: FormValues['country']) => {
  return props.withUserInformation || userInformationWithNationalId(country)
}
</script>
